import { useEffect, useRef, useState } from "react";

/**
 * Hook for scroll-triggered reveal animations.
 * Returns a ref to attach to the element and a boolean `isVisible`.
 * Options:
 *   threshold: 0-1, how much of the element must be visible (default 0.15)
 *   rootMargin: CSS margin string for early/late trigger (default "0px 0px -40px 0px")
 */
const useScrollReveal = ({ threshold = 0.15, rootMargin = "0px 0px -40px 0px" } = {}) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold, rootMargin }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold, rootMargin]);

  return { ref, isVisible };
};

export default useScrollReveal;
